import { message, notification } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';

interface ToastOptions {
  title?: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

/**
 * Enhanced toast notifications with better UX
 */
export const toast = {
  /**
   * Success notification with optional action button
   */
  success: (options: ToastOptions) => {
    const { title = 'Success', description, action, duration = 4 } = options;

    notification.success({
      message: title,
      description,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      placement: 'top',
      duration,
      btn: action ? (
        <button
          onClick={() => {
            action.onClick();
            notification.destroy();
          }}
          style={{
            padding: '4px 12px',
            border: '1px solid #52c41a',
            background: 'transparent',
            color: '#52c41a',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          {action.label}
        </button>
      ) : undefined,
    });
  },

  /**
   * Error notification with optional action button
   */
  error: (options: ToastOptions) => {
    const { title = 'Error', description, action, duration = 6 } = options;

    notification.error({
      message: title,
      description,
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      placement: 'top',
      duration,
      btn: action ? (
        <button
          onClick={() => {
            action.onClick();
            notification.destroy();
          }}
          style={{
            padding: '4px 12px',
            border: '1px solid #ff4d4f',
            background: 'transparent',
            color: '#ff4d4f',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          {action.label}
        </button>
      ) : undefined,
    });
  },

  /**
   * Info notification
   */
  info: (options: ToastOptions) => {
    const { title = 'Info', description, action, duration = 4 } = options;

    notification.info({
      message: title,
      description,
      icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
      placement: 'top',
      duration,
      btn: action ? (
        <button
          onClick={() => {
            action.onClick();
            notification.destroy();
          }}
          style={{
            padding: '4px 12px',
            border: '1px solid #1890ff',
            background: 'transparent',
            color: '#1890ff',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          {action.label}
        </button>
      ) : undefined,
    });
  },

  /**
   * Warning notification
   */
  warning: (options: ToastOptions) => {
    const { title = 'Warning', description, action, duration = 5 } = options;

    notification.warning({
      message: title,
      description,
      icon: <WarningOutlined style={{ color: '#faad14' }} />,
      placement: 'top',
      duration,
      btn: action ? (
        <button
          onClick={() => {
            action.onClick();
            notification.destroy();
          }}
          style={{
            padding: '4px 12px',
            border: '1px solid #faad14',
            background: 'transparent',
            color: '#faad14',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          {action.label}
        </button>
      ) : undefined,
    });
  },

  /**
   * Promise-based notification for async operations
   */
  promise: async <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ): Promise<T> => {
    const hide = message.loading(messages.loading, 0);

    try {
      const result = await promise;
      hide();
      toast.success({
        description:
          typeof messages.success === 'function'
            ? messages.success(result)
            : messages.success,
      });
      return result;
    } catch (error) {
      hide();
      toast.error({
        description:
          typeof messages.error === 'function'
            ? messages.error(error)
            : messages.error,
      });
      throw error;
    }
  },
};

// Configure default notification settings
notification.config({
  placement: 'top',
  top: 24,
  duration: 4,
  maxCount: 3,
});
